import React, { useState, useEffect, useRef } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { TextField, Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReactDOM from 'react-dom';

import { axiosInstance } from '../../../utils/tokenUtils';
import './PauseAvailabilityModal.css';
import { toast } from 'react-hot-toast';
import { triggerCalendarRefresh } from '../../../utils/calendarRefresh';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import DeleteIcon from '../../../assets/ScheduleIcon/delete.svg';
import EditIcon from '../../../assets/ScheduleIcon/edit.svg';
import CalendarIcon from '../../../assets/ScheduleIcon/calendar.svg';
import ClockIcon from '../../../assets/ScheduleIcon/clock.svg';

// Setup dayjs plugins for timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom renderer that hides the minute hand in the hours view
const customTimeViewRenderer = props => {
  const { view, value, onChange, ampm, ...other } = props;

  // For hours view, render the standard clock
  if (view === 'hours') {
    return renderTimeViewClock({
      view,
      value,
      onChange,
      ampm,
      ...other,
    });
  }

  // For minutes view, which is shown after hours are selected
  return renderTimeViewClock({
    view,
    value,
    onChange,
    ampm,
    ...other,
  });
};

const PauseAvailabilityModel = ({ open, onClose, onConfirm }) => {
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [pausedSessions, setPausedSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  // State for calendar
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarType, setCalendarType] = useState(null); // 'start' or 'end'
  const [calendarDate, setCalendarDate] = useState(new Date());

  // New state variables for time picker
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const [clockView, setClockView] = useState('hours');

  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const calendarRef = useRef(null);
  const timePickerRef = useRef(null);

  // Fetch paused sessions when the component mounts or when it's opened
  useEffect(() => {
    if (open) {
      fetchPausedSessions();
    }
  }, [open]);

  // Add event listener to detect clicks outside calendar and time picker
  useEffect(() => {
    const handleClickOutside = event => {
      // Close calendar if clicking outside
      if (showCalendar && calendarRef.current && !calendarRef.current.contains(event.target)) {
        // Check if the click was on a calendar trigger element
        const isCalendarTrigger =
          (startDateRef.current && startDateRef.current.contains(event.target)) ||
          (endDateRef.current && endDateRef.current.contains(event.target));

        if (!isCalendarTrigger) {
          setShowCalendar(false);
        }
      }

      // Close time picker if clicking outside
      if (
        activeTimePicker &&
        timePickerRef.current &&
        !timePickerRef.current.contains(event.target)
      ) {
        // Check if the click was on a time picker trigger element
        const isTimePickerTrigger =
          (startTimeRef.current && startTimeRef.current.contains(event.target)) ||
          (endTimeRef.current && endTimeRef.current.contains(event.target));

        if (!isTimePickerTrigger) {
          setActiveTimePicker(null);
          setClockView('hours');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar, activeTimePicker]);

  // Add window resize listener to update time picker position
  useEffect(() => {
    const handleResize = () => {
      if (activeTimePicker) {
        const field = activeTimePicker.field;
        const ref = field === 'start' ? startTimeRef : endTimeRef;

        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const isMobile = window.innerWidth <= 576;

          if (isMobile) {
            setCalendarPosition({
              top: rect.bottom + window.scrollY + 5,
              left: window.innerWidth / 2,
            });
          } else {
            setCalendarPosition({
              top: rect.bottom + window.scrollY + 5,
              left: rect.left + window.scrollX,
            });
          }
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [activeTimePicker]);

  // Function to fetch paused sessions from the API
  const fetchPausedSessions = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/teachers/pause-availability?offset=0&limit=100');
      const payload = response.data?.data || response.data;
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setPausedSessions(
        items.map(session => {
          // Convert UTC times to local time
          const startUtc = dayjs.utc(session.start); // Parse as UTC
          const endUtc = dayjs.utc(session.end); // Parse as UTC

          return {
            id: session.id,
            startDate: startUtc.local(), // Convert to local
            startTime: startUtc.local(), // Convert to local
            endDate: endUtc.local(), // Convert to local
            endTime: endUtc.local(), // Convert to local
          };
        })
      );
      setLoading(false);
    } catch (error) {
      console.error('Error fetching paused sessions:', error);
      toast.error('Failed to fetch paused sessions');
      setLoading(false);
    }
  };

  // Check if the form is valid - all fields filled
  const isFormValid = () => {
    return startDate && startTime && endDate && endTime;
  };

  const handleAddMore = () => {
    setStartDate(null);
    setStartTime(null);
    setEndDate(null);
    setEndTime(null);
    setEditingIndex(null);
    setShowDatePicker(true);
  };

  // Function to create a combined ISO datetime string
  const combineDateAndTime = (date, time) => {
    if (!date || !time) return null;

    // Get the date components
    const year = date.year();
    const month = date.month() + 1; // Month is 0-indexed in dayjs
    const day = date.date();

    // Get the time components
    let hours, minutes, seconds;

    if (typeof time === 'string') {
      // Parse the time string (hh:mm A format)
      const timeObj = dayjs(`2023-01-01 ${time}`, 'YYYY-MM-DD hh:mm A');
      hours = timeObj.hour();
      minutes = timeObj.minute();
      seconds = 0;
    } else {
      // Use the dayjs object directly
      hours = time.hour();
      minutes = time.minute();
      seconds = 0;
    }

    // Create a local dayjs object with the specified date and time
    const localDateTime = dayjs()
      .year(year)
      .month(month - 1)
      .date(day)
      .hour(hours)
      .minute(minutes)
      .second(seconds);

    // Convert to UTC and format as required by the API
    return localDateTime.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
  };

  const handleSave = async () => {
    if (isFormValid()) {
      try {
        setLoading(true);

        const start_date_time = combineDateAndTime(startDate, startTime);
        const end_date_time = combineDateAndTime(endDate, endTime);

        // Validate that end time is after start time
        if (dayjs(end_date_time).isBefore(dayjs(start_date_time))) {
          toast.error('End time must be after start time');
          setLoading(false);
          return;
        }

        // Create request payload
        const payload = {
          start_date_time,
          end_date_time,
        };

        if (editingIndex !== null) {
          // For editing an existing session using PATCH instead of PUT
          const sessionId = pausedSessions[editingIndex].id;
          await axiosInstance.patch(`/teachers/pause-availability/${sessionId}`, payload);
          toast.success('Paused availability updated successfully');
        } else {
          // For creating a new session
          await axiosInstance.post('/teachers/pause-availability', payload);
          toast.success('Paused availability created successfully');
        }

        // Refresh the sessions list
        await fetchPausedSessions();

        // Trigger calendar refresh if available
        triggerCalendarRefresh();

        setShowDatePicker(false);
        setEditingIndex(null);
        setLoading(false);
      } catch (error) {
        console.error('Error saving paused session:', error);
        if (error.response?.data?.detail) {
          // Extract validation error message
          if (Array.isArray(error.response.data.detail)) {
            const errorMsg =
              error.response.data.detail[0]?.msg || 'Failed to save paused availability';
            toast.error(errorMsg);
          } else {
            toast.error(error.response.data.detail);
          }
        } else {
          toast.error(
            editingIndex !== null
              ? 'Failed to update paused availability'
              : 'Failed to create paused availability'
          );
        }
        setLoading(false);
      }
    }
  };

  const handleEdit = index => {
    const session = pausedSessions[index];
    setStartDate(session.startDate);
    setStartTime(session.startTime);
    setEndDate(session.endDate);
    setEndTime(session.endTime);
    setEditingIndex(index);
    setShowDatePicker(true);
  };

  const handleDelete = async index => {
    try {
      setLoading(true);
      const sessionId = pausedSessions[index].id;
      await axiosInstance.delete(`/teachers/pause-availability/${sessionId}`);
      toast.success('Paused availability deleted successfully');

      // Refresh the sessions list
      await fetchPausedSessions();

      // Trigger calendar refresh if available
      triggerCalendarRefresh();

      setLoading(false);
    } catch (error) {
      console.error('Error deleting paused session:', error);
      toast.error('Failed to delete paused availability');
      setLoading(false);
    }
  };

  const handleConfirmPause = () => {
    onConfirm(pausedSessions);

    // Trigger calendar refresh if available
    triggerCalendarRefresh();

    setShowConfirmation(false);
    onClose();
  };

  const formatDate = date => {
    if (!date) return '';
    return date.format('MMM DD, YYYY');
  };

  // Format time to display in the format shown in the sessions list
  const formatDisplayTime = time => {
    if (!time) return '';
    let formattedTime = '';
    if (typeof time === 'string') {
      formattedTime = time;
    } else {
      formattedTime = time.format('hh:mmA'); // Format as 12:00AM
    }
    return formattedTime;
  };

  // Utility function to convert string time to dayjs object
  const stringToTime = timeString => {
    if (!timeString) return null;
    try {
      // Handle different time formats
      if (typeof timeString === 'string') {
        if (timeString.includes('AM') || timeString.includes('PM')) {
          return dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
        } else {
          const [hours, minutes] = timeString.split(':');
          return dayjs().hour(hours).minute(minutes);
        }
      }
      // Handle dayjs object
      return timeString;
    } catch (error) {
      console.error('Failed to parse time:', error);
      return null;
    }
  };

  // Function to open the calendar for date selection
  const handleOpenCalendar = type => {
    // Close any open time picker to avoid conflicts
    setActiveTimePicker(null);

    setCalendarType(type);

    // Get the reference to the date input element
    const ref = type === 'start' ? startDateRef : endDateRef;

    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();

      // Calculate position for the calendar
      setCalendarPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }

    setShowCalendar(true);
  };

  // Function to handle date selection from calendar
  const handleCalendarDateSelect = date => {
    if (calendarType === 'start') {
      setStartDate(dayjs(date));
    } else if (calendarType === 'end') {
      setEndDate(dayjs(date));
    }
    setShowCalendar(false);
  };

  // Function to handle opening a time picker
  const handleTimePickerOpen = field => {
    // Close calendar if it's open to avoid conflicts
    setShowCalendar(false);

    // Set default time if none is selected
    if (field === 'start' && !startTime) {
      setStartTime(dayjs().hour(9).minute(0));
    } else if (field === 'end' && !endTime) {
      setEndTime(dayjs().hour(10).minute(0));
    }

    // Set active time picker field
    setActiveTimePicker({
      field,
      previousValue: field === 'start' ? startTime : endTime,
    });

    // Always start with hours view
    setClockView('hours');
  };

  // Function to handle closing a time picker
  const handleTimePickerClose = () => {
    // If user cancels without selecting a new value, restore the previous value
    if (activeTimePicker?.previousValue) {
      const { field, previousValue } = activeTimePicker;
      if (field === 'start' && !startTime) {
        setStartTime(previousValue);
      } else if (field === 'end' && !endTime) {
        setEndTime(previousValue);
      }
    }

    setActiveTimePicker(null);
    setClockView('hours'); // Reset to hours view
  };

  // Updated time change handler with hour/minute sequential selection
  const handleTimeChange = (field, value) => {
    const setter = field === 'start' ? setStartTime : setEndTime;
    const currentValue = field === 'start' ? startTime : endTime;

    // If value is null, clear the field
    if (!value) {
      setter(null);
      setClockView('hours');
      return;
    }

    // For hours selection, update the hours and keep existing minutes or use 00
    if (clockView === 'hours') {
      if (currentValue) {
        const existingTime = stringToTime(currentValue);
        if (existingTime) {
          // Keep the existing minutes but update the hours
          const updatedTime = value.minute(existingTime.minute());
          setter(updatedTime);
        } else {
          // If parsing failed, use 00 minutes with the new hour
          setter(value.minute(0));
        }
      } else {
        // If no existing time, use 00 minutes with the new hour
        setter(value.minute(0));
      }

      // Switch to minutes view for optional selection
      setClockView('minutes');
      return;
    }

    // For minutes selection, ensure we round to nearest 15 minutes
    const minutes = value.minute();
    const roundedMinutes = Math.round(minutes / 15) * 15;

    // Create a new dayjs object with rounded minutes
    const roundedTime = value.minute(roundedMinutes === 60 ? 0 : roundedMinutes);
    // If rounding to 60, increment the hour
    const adjustedTime = roundedMinutes === 60 ? roundedTime.add(1, 'hour') : roundedTime;

    setter(adjustedTime);
    setClockView('hours'); // Reset for next selection

    // Close the picker after minutes are selected
    setActiveTimePicker(null);
  };

  // Render the calendar with portal to ensure it's not constrained by modal container
  const renderCalendarWithPortal = () => {
    if (!showCalendar) return null;

    const calendar = (
      <div
        ref={calendarRef}
        className="pause-calendar-popup"
        style={{
          top: `${calendarPosition.top}px`,
          left: `${calendarPosition.left}px`,
          position: 'fixed',
          zIndex: 2000,
        }}
      >
        <div className="pause-calendar-header">
          <button
            onClick={() =>
              setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))
            }
          >
            &lt;
          </button>
          <div className="pause-calendar-title">
            <div>{new Intl.DateTimeFormat('en-US', { month: 'long' }).format(calendarDate)}</div>
            <div className="pause-calendar-year">{calendarDate.getFullYear()}</div>
          </div>
          <button
            onClick={() =>
              setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))
            }
          >
            &gt;
          </button>
        </div>
        <div className="pause-calendar-days-header">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>
        <div className="pause-calendar-grid">{renderCalendarWeeks()}</div>
        <div className="pause-calendar-footer">
          <button
            className="pause-today-button"
            onClick={() => handleCalendarDateSelect(new Date())}
          >
            Today
          </button>
        </div>
      </div>
    );

    return ReactDOM.createPortal(calendar, document.body);
  };

  // Render time picker with portal
  const renderTimePickerWithPortal = () => {
    if (!activeTimePicker) return null;

    const { field } = activeTimePicker;
    const timeValue = field === 'start' ? startTime : endTime;
    const isMobile = window.innerWidth <= 576;

    // Instead of creating our own portal, use the Material-UI system directly
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={timeValue}
          onChange={value => handleTimeChange(field, value)}
          className="pause-time-picker"
          slots={{
            textField: () => null, // Don't render an input field
          }}
          ampm={true}
          format="hh:mm A"
          minutesStep={15}
          viewRenderers={{
            hours: customTimeViewRenderer,
            minutes: customTimeViewRenderer,
          }}
          views={['hours', 'minutes']}
          view={clockView}
          onViewChange={view => setClockView(view)}
          open={true}
          onClose={handleTimePickerClose}
          timeSteps={{ minutes: 15 }}
          slotProps={{
            popper: {
              style: { zIndex: 2500 },
              placement: 'bottom-start',
              anchorEl: field === 'start' ? startTimeRef.current : endTimeRef.current,
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 5], // Add a small offset so it doesn't touch the input
                  },
                },
              ],
            },
            toolbar: {
              hidden: true, // Hide toolbar for cleaner look
            },
            actionBar: {
              actions: [], // No actions in the action bar
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  // Helper method to render calendar weeks
  const renderCalendarWeeks = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    const firstDayOfWeek = firstDay.getDay();
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      const day = prevMonthLastDay - firstDayOfWeek + i + 1;
      const date = new Date(year, month - 1, day);
      days.push({
        day,
        month: month - 1,
        year,
        isCurrentMonth: false,
        date: date,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
        date: date,
      });
    }

    const remainingDays = 7 - (days.length % 7 || 7);
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        day: i,
        month: month + 1,
        year,
        isCurrentMonth: false,
        date: date,
      });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return weeks.map((week, weekIndex) => (
      <div key={weekIndex} className="pause-calendar-week">
        {week.map((day, dayIndex) => {
          let isSelected = false;
          if (calendarType === 'start' && startDate) {
            isSelected = day.date.toDateString() === new Date(startDate.toString()).toDateString();
          } else if (calendarType === 'end' && endDate) {
            isSelected = day.date.toDateString() === new Date(endDate.toString()).toDateString();
          }

          return (
            <div
              key={dayIndex}
              className={`pause-calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => handleCalendarDateSelect(day.date)}
            >
              {day.day}
            </div>
          );
        })}
      </div>
    ));
  };

  // Custom render functions for date and time inputs
  const renderDateInput = ({ inputRef, inputProps, InputProps, ...other }) => {
    // Extract the onClick handler from inputProps
    const { onClick } = inputProps || {};

    return (
      <div
        className="pause-input-wrapper"
        onClick={e => {
          e.stopPropagation();
          if (onClick) onClick(e);
        }}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          padding: '8px 10px',
          border: '1px solid var(--Stroke, #d5dee3)',
          borderRadius: '8px',
          backgroundColor: 'white',
          boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={e => {
          e.currentTarget.style.borderColor = '#0a5ef3';
          e.currentTarget.style.boxShadow = '0px 0px 0px 1px rgba(10, 94, 243, 0.2)';
        }}
        onMouseOut={e => {
          e.currentTarget.style.borderColor = 'var(--Stroke, #d5dee3)';
          e.currentTarget.style.boxShadow = '0px 1px 2px 0px rgba(10, 13, 18, 0.05)';
        }}
      >
        <div
          className="pause-input-icon-custom"
          onClick={e => {
            e.stopPropagation();
            if (onClick) onClick(e);
          }}
          style={{
            cursor: 'pointer',
            marginRight: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={CalendarIcon} alt="Calendar" />
        </div>
        <input
          ref={inputRef}
          {...other}
          placeholder="MM/DD/YY"
          readOnly={true}
          onClick={e => {
            e.stopPropagation();
            if (onClick) onClick(e);
          }}
          style={{
            border: 'none',
            outline: 'none',
            width: '100%',
            fontFamily: 'Poppins',
            fontSize: '12px',
            fontWeight: 500,
            color: '#1F435D',
            cursor: 'pointer',
            caretColor: 'transparent',
            userSelect: 'none',
            backgroundColor: 'transparent',
          }}
        />
      </div>
    );
  };

  const renderTimeInput = ({ inputRef, inputProps, InputProps, ...other }) => {
    // Extract the field type from placeholder
    const fieldType = other.placeholder && other.placeholder.includes('Start') ? 'start' : 'end';

    // Get the current value for display
    const value =
      fieldType === 'start'
        ? startTime
          ? startTime.format('hh:mm A')
          : ''
        : endTime
          ? endTime.format('hh:mm A')
          : '';

    return (
      <div
        className="pause-input-wrapper"
        onClick={e => {
          e.stopPropagation();
          handleTimePickerOpen(fieldType);
        }}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          padding: '8px 10px',
          border: '1px solid var(--Stroke, #d5dee3)',
          borderRadius: '8px',
          backgroundColor: 'white',
          boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={e => {
          e.currentTarget.style.borderColor = '#0a5ef3';
          e.currentTarget.style.boxShadow = '0px 0px 0px 1px rgba(10, 94, 243, 0.2)';
        }}
        onMouseOut={e => {
          e.currentTarget.style.borderColor = 'var(--Stroke, #d5dee3)';
          e.currentTarget.style.boxShadow = '0px 1px 2px 0px rgba(10, 13, 18, 0.05)';
        }}
      >
        <div
          className="pause-input-icon-custom"
          onClick={e => {
            e.stopPropagation();
            handleTimePickerOpen(fieldType);
          }}
          style={{
            cursor: 'pointer',
            marginRight: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={ClockIcon} alt="Clock" />
        </div>
        <input
          ref={inputRef}
          placeholder={other.placeholder || (fieldType === 'start' ? 'Start Time' : 'End Time')}
          value={value}
          readOnly={true}
          onClick={e => {
            e.stopPropagation();
            handleTimePickerOpen(fieldType);
          }}
          style={{
            border: 'none',
            outline: 'none',
            width: '100%',
            fontFamily: 'Poppins',
            fontSize: '12px',
            fontWeight: 500,
            color: '#1F435D',
            cursor: 'pointer',
            backgroundColor: 'transparent',
            caretColor: 'transparent',
            userSelect: 'none',
          }}
        />
      </div>
    );
  };

  if (!open) return null;

  if (showConfirmation) {
    return (
      <div className="modal-overlay">
        <div className="modal confirmation-modal">
          <div className="modal-header">
            <IconButton className="close-button" onClick={() => setShowConfirmation(false)}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className="confirmation-content">
            <div className="info-icon-container">
              <InfoOutlinedIcon className="info-icon" />
            </div>
            <h2>Pausing Availability</h2>
            <p className="confirmation-text">
              By Pausing availability, you're Sessions wouldn't be visible to the students. You have
              to disable paused sessions to be visible again.
            </p>
            <div className="confirmation-buttons">
              <button className="dismiss-button" onClick={() => setShowConfirmation(false)}>
                Dismiss
              </button>
              <button className="confirm-button" onClick={handleConfirmPause}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="pause-modal">
        <div className="modal-header">
          <Typography variant="h6">Pause Availability</Typography>
          <IconButton className="close-button" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="modal-content-pause">
          {/* Add more button at the top - only show when form is not visible */}
          {!showDatePicker && (
            <div className="add-more-row">
              <button className="add-more-button" onClick={handleAddMore}>
                + Add more
              </button>
            </div>
          )}

          {/* Date & Time picker section */}
          {showDatePicker && (
            <div className="date-time-section">
              <Box className="date-time-header">Date & Time</Box>
              <div className="date-time-container">
                <div className="date-time-row-single">
                  <div className="date-time-field">
                    <label className="subheading-label">Start Date</label>
                    <div
                      className="date-select-field"
                      onClick={() => handleOpenCalendar('start')}
                      ref={startDateRef}
                    >
                      {renderDateInput({
                        value: startDate ? startDate.format('MM/DD/YY') : '',
                        inputProps: {
                          onClick: () => handleOpenCalendar('start'),
                        },
                      })}
                    </div>
                  </div>

                  <div className="date-time-field">
                    <label className="subheading-label">Start Time</label>
                    <div className="time-field-wrapper" ref={startTimeRef}>
                      {renderTimeInput({
                        placeholder: 'Start Time',
                      })}
                    </div>
                  </div>

                  <div className="date-time-field">
                    <label className="subheading-label">End Date</label>
                    <div
                      className="date-select-field"
                      onClick={() => handleOpenCalendar('end')}
                      ref={endDateRef}
                    >
                      {renderDateInput({
                        value: endDate ? endDate.format('MM/DD/YY') : '',
                        inputProps: {
                          onClick: () => handleOpenCalendar('end'),
                        },
                      })}
                    </div>
                  </div>

                  <div className="date-time-field">
                    <label className="subheading-label">End Time</label>
                    <div className="time-field-wrapper" ref={endTimeRef}>
                      {renderTimeInput({
                        placeholder: 'End Time',
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="action-buttons-pause">
                <button
                  className="save-button-pause"
                  onClick={handleSave}
                  disabled={!isFormValid() || loading}
                  style={{
                    background:
                      isFormValid() && !loading
                        ? 'var(--Primary, #0A5EF3)'
                        : 'var(--Stroke, #d5dee3)',
                    cursor: isFormValid() && !loading ? 'pointer' : 'not-allowed',
                  }}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="cancel-button-pause"
                  onClick={() => setShowDatePicker(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Paused Sessions section */}
          {loading ? (
            <div className="no-sessions">
              <label className="date-time-header">Paused Sessions</label>
              <p>Loading sessions...</p>
            </div>
          ) : pausedSessions.length === 0 ? (
            <div className="no-sessions">
              <label className="date-time-header">Paused Sessions</label>
              <p>No sessions paused</p>
            </div>
          ) : (
            <div className="sessions-container">
              <label className="date-time-header">Paused Sessions</label>
              {pausedSessions.map((session, index) => (
                <div key={index} className="session-item">
                  <div className="session-details">
                    <div className="session-from">
                      <span className="label">From</span>
                      <span className="date-time">
                        {formatDate(session.startDate)} ({formatDisplayTime(session.startTime)})
                      </span>
                    </div>
                    <div className="session-to">
                      <span className="label">To</span>
                      <span className="date-time">
                        {formatDate(session.endDate)} ({formatDisplayTime(session.endTime)})
                      </span>
                    </div>
                  </div>
                  <div className="session-actions">
                    <IconButton onClick={() => handleEdit(index)} disabled={loading}>
                      <img src={EditIcon} alt="Edit" />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(index)} disabled={loading}>
                      <img
                        src={DeleteIcon}
                        alt="Delete"
                        style={{
                          width: '20px',
                          height: '20px',
                          color: '#1F435D',
                        }}
                      />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {renderCalendarWithPortal()}
      {renderTimePickerWithPortal()}
    </div>
  );
};

export default PauseAvailabilityModel;
